import React from 'react';
import {
  DataGridProps,
  RenderCellProps,
  RenderEditCellProps,
  textEditor,
} from 'react-data-grid';

import { Text } from '../../../core-ui';
import formatCurrency from '../../../core/support/formatCurrency';
import { parseInput } from '../../uiold/CurrencyInput';
import { FlatFeesCell } from './FlatFeesCell';
import { Row } from './helpers';
import { GetFeesAndSales } from './__generated__/GetFeesAndSales';

export function makeColumns(input: {
  queryData: GetFeesAndSales;
  onFlatFeeClick: (row: Row) => void;
}): DataGridProps<Row>['columns'] & { isPercentageCol?: boolean } {
  const { queryData, onFlatFeeClick } = input;
  const {
    myAccount: {
      settings: { currency },
    },
    redemptions,
  } = queryData;

  return [
    {
      key: 'vendorName',
      name: t('Vendor Name'),
      frozen: true,
    },
    {
      key: 'flatFeeTotal',
      name: t('Flat fees'),
      renderCell: ({ row }) => (
        <FlatFeesCell
          currency={currency ?? 'USD'}
          onCellClick={(row) => onFlatFeeClick(row)}
          row={row}
        />
      ),
    },
    {
      key: 'salesTotal',
      name: t('Sales Total'),
      frozen: true,
      renderCell: ({ row }) => {
        return <Text>{formatCurrency(row.salesTotal, currency ?? 'USD')}</Text>;
      },
    },
    {
      key: 'sales',
      name: t('Sales'),
      renderCell: ({ row }: RenderCellProps<Row, unknown>) => {
        const value = row?.sales ?? '0';
        const number = parseInput(value);
        return (
          <Text paddingInline="8px">
            {formatCurrency(number, currency ?? undefined)}
          </Text>
        );
      },
      renderEditCell: ({
        row,
        column,
        onRowChange,
        onClose,
      }: RenderEditCellProps<Row, unknown>) =>
        textEditor({
          row,
          column,
          onRowChange,
          onClose,
        }),
    },
    {
      key: 'category',
      name: t('Category'),
      renderCell: ({ row }) => {
        return <Text>{row.category.name}</Text>;
      },
    },
    {
      key: 'percentage',
      name: '%',
      renderCell: ({ row }) => {
        return <Text>{row.customPercentage ?? row.percentage}</Text>;
      },
      renderEditCell: ({
        row,
        column,
        onRowChange,
        onClose,
      }: RenderEditCellProps<Row, unknown>) =>
        textEditor({
          row,
          column,
          onRowChange,
          onClose,
        }),
    },
    {
      key: 'pctOfSales',
      name: t('% of Sales'),
      renderCell: ({ row }) => (
        <Text paddingInline="8px">
          {formatCurrency(row.pctOfSales, currency ?? undefined)}
        </Text>
      ),
      cellClass: 'no-padding-inline',
    },
    ...(redemptions ?? []).map((redemption) => {
      return {
        key: redemption.id,
        name: redemption.name,
        type: 'redemption',
        invoiceTotalModifier: redemption.invoiceTotalModifier,
        renderHeaderCell: () => (
          <Text
            paddingInline="8px"
            backgroundColor={
              redemption.invoiceTotalModifier === 'subtract'
                ? 'red.100'
                : redemption.invoiceTotalModifier === 'add'
                ? 'green.100'
                : undefined
            }
          >
            {redemption.name}
          </Text>
        ),
        headerCellClass: 'no-padding-inline',
        renderCell: ({ row }: RenderCellProps<Row, unknown>) => {
          const value = row?.redemptions[redemption.id] ?? '0';
          const number = parseInput(String(value));
          return (
            <Text paddingInline="8px">
              {formatCurrency(number, currency ?? undefined)}
            </Text>
          );
        },
        renderEditCell: ({
          row,
          column,
          onRowChange,
          onClose,
        }: RenderEditCellProps<Row, unknown>) =>
          textEditor({
            row,
            column,
            onRowChange: (row) => {
              const redemptionIdsSet = new Set(Object.keys(row?.redemptions));
              const redemptionColKey = Object.keys(row).find((key) =>
                redemptionIdsSet.has(key),
              );
              if (redemptionColKey) {
                const { [redemptionColKey]: redemptionValue, ...rest } = row;
                const newRedemptions = {
                  ...row.redemptions,
                  [redemptionColKey]: redemptionValue,
                };
                const newRow = { ...rest, redemptions: newRedemptions } as Row;
                return onRowChange(newRow);
              }
              return onRowChange(row);
            },
            onClose,
          }),
        cellClass: 'no-padding-inline',
      };
    }),
    {
      key: 'invoiceTotal',
      name: t('Invoice Total'),
      frozen: true,
      renderCell: ({ row }) => {
        return (
          <Text>{formatCurrency(row.invoiceTotal, currency ?? 'USD')}</Text>
        );
      },
    },
  ];
}
